import { buildLogger } from '@instinet-analytics/core-lib';
import { selectActiveWidgetIds } from 'layout/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { PortfolioService } from 'portfolio/PortfolioService';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import { WidgetsService } from 'widgets/WidgetsService';
import without from 'lodash/without';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService';
import { getSymbolTilesMessageHandler } from 'widgets/symbolTile/stream/symbolTilesMessageHandler';
import { SymbolTilesMessage } from 'communication/types/symbolTiles/SymbolTilesElement';
import { SymbolTilesStreamRequest } from 'communication/types/symbolTiles/SymbolTilesStreamRequest';
import { symbolTilesRequestBatchSanitizer } from 'widgets/symbolTile/stream/symbolTilesRequestBatchSanitizer';
import { noSymbol } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataAggregator';
import { WidgetCustomStreamManagerSupportingRegisteredSymbolsBase } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManagerSupportingRegisteredSymbols';
import { WidgetCustomManagedStreamOptions } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import uniq from 'lodash/uniq';
import { createRemoveSymbolTilesStreamAction } from 'widgets/symbolTile/store/data/actions';
import orderBy from 'lodash/orderBy';
import { selectConfiguredSymbolForWidgetOrItsCapsuleContainer } from 'widgets/tiles/store/selectors';
import { WidgetMarketDataRequestManager } from 'marketData/WidgetMarketDataRequestManager';
import {
    buildBarRequest,
    buildTickRequest,
} from 'marketData/MarketDataRequest';
import flatten from 'lodash/flatten';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { SymbolsService } from 'symbols/SymbolsService';

export const SymbolTilesStreamKey = 'SymbolTilesStream';
const RetainStaleSymbolsQty = 2;

export const SymbolTileMarketDataBarSize = 5;

/*
 * Custom manager for the one stream that handles all symbol tile widgets
 * Handles individual symbol tile widgets, and capsules within a My Portfolio Capsules widgets
 */
export class SymbolTilesStreamManager extends WidgetCustomStreamManagerSupportingRegisteredSymbolsBase {
    private readonly portfolioService: PortfolioService;
    private readonly symbolsService: SymbolsService;
    private readonly widgetsService: WidgetsService;
    private readonly streamDataService: StreamDataService;
    private readonly messageHandlers: StreamDataHandlers<SymbolTilesMessage>;
    private readonly marketDataRequestManager: WidgetMarketDataRequestManager;

    private isStreaming = false;
    private symbols: string[] = [];
    private removedSymbols: { symbol: string; time: number }[] = [];

    public constructor(options: WidgetCustomManagedStreamOptions) {
        super(
            options.store,
            buildLogger('SymbolTilesStreamManager', options.loggingService)
        );
        this.portfolioService = options.portfolioService;
        this.streamDataService = options.streamDataService;
        this.widgetsService = options.widgetsService;
        this.messageHandlers = getSymbolTilesMessageHandler(this.store);
        this.streamDataService.registerRequestBatchSanitizer(
            options.channelDestination,
            symbolTilesRequestBatchSanitizer
        );
        this.marketDataRequestManager = options.marketDataRequestManager;
        this.symbolsService = options.dataServices.symbolsService;
    }

    public resetState() {
        this.symbols = [];
        this.removedSymbols = [];
        this.isStreaming = false;
    }

    public checkStream(reason: string, dispatcher: ActionsDispatcher): void {
        this.logger.debug('Checking stream', reason);
        const { symbology: usersSymbology } = selectAppSettings(
            this.store.getState()
        );

        this.getActiveSymbolsFromState().then((nextSymbols) => {
            const adding = without(nextSymbols, ...this.symbols);
            const removing = without(this.symbols, ...nextSymbols);

            this.marketDataRequestManager.register(
                'SymbolTilesStreamManager',
                flatten(
                    nextSymbols.map((s) => [
                        buildBarRequest(
                            s,
                            usersSymbology,
                            SymbolTileMarketDataBarSize
                        ),
                        buildTickRequest(s, usersSymbology),
                    ])
                )
            );

            this.removeStaleSymbols(dispatcher, adding, removing);

            this.logger.debug('Checked', [adding, removing]);

            this.symbols = nextSymbols;

            if (nextSymbols.length === 0) {
                this.stopStreaming(dispatcher, reason);
            } else if (adding.length > 0 || removing.length > 0) {
                this.startOrUpdateStream(dispatcher, adding, removing);
            }
        });
    }

    public getInfo(): Record<string, unknown> {
        return {
            type: 'SymbolTilesStreamManager',
            registeredSymbolStreams: this.registeredSymbolStreams,
            widgetIds: this.widgetIds,
            symbols: this.symbols,
        };
    }

    protected startOrUpdateStream(
        dispatcher: ActionsDispatcher,
        addSyms: string[],
        delSyms: string[]
    ) {
        this.logger.debug('Starting/updating stream', {
            addCols: addSyms,
            removeCols: delSyms,
        });

        const state = this.store.getState();

        const currentChannelId = selectChannelId(state, SymbolTilesStreamKey);

        const { symbology } = selectAppSettings(state);

        const payload: SymbolTilesStreamRequest = {
            addSyms: addSyms.join('|'),
            delSyms: delSyms.join('|'),
            symbology,
        };

        (async () => {
            await this.streamDataService.startAndWatchStream(
                SymbolTilesStreamKey,
                'startSymbolTilesStream',
                this.messageHandlers,
                currentChannelId,
                dispatcher
            )(payload);

            this.isStreaming = true;
        })();
    }

    protected stopStreaming(dispatcher: ActionsDispatcher, reason: string) {
        if (!this.isStreaming) {
            return;
        }

        const stopChannelDestination = 'stopSymbolTilesStream';

        this.logger.debug('Stopping streaming ' + stopChannelDestination, {
            reason,
            streamKey: SymbolTilesStreamKey,
        });

        // Stop the stream but we keep the channel listener and data to ensure we don't miss messages (as we reuse the channel)...
        this.streamDataService.stopStream(
            SymbolTilesStreamKey,
            dispatcher,
            false
        );
        this.isStreaming = false;
    }

    private async getActiveSymbolsFromState(): Promise<string[]> {
        const state = this.store.getState();
        const activeWidgetIds = selectActiveWidgetIds(state);
        const { symbology: usersSymbology } = selectAppSettings(state);

        const activeRegisteredWidgetIds = this.widgetIds.filter(
            (a) => activeWidgetIds.indexOf(a.widgetKey) > -1
        );

        const selection = selectPortfolioSelection(state);
        const selectedSymbol = new PortfolioSelectionWrapper(selection)
            .selectedSymbol;
        // Convert the selected symbol to the users symbology...
        const selectedSymbolInUsersSymbology = selectedSymbol
            ? await this.symbolsService.convertSymbol(
                  selectedSymbol,
                  selection!.symbology,
                  usersSymbology
              )
            : undefined;

        // Configured symbols are by definition in the users symbology...
        const symbols = activeRegisteredWidgetIds
            .map((i) => {
                const configuredSymbol =
                    selectConfiguredSymbolForWidgetOrItsCapsuleContainer(
                        state,
                        i
                    );

                return configuredSymbol
                    ? configuredSymbol
                    : selectedSymbolInUsersSymbology;
            })
            .filter((s) => !!s) as string[];

        const activeRegisteredSymbolStreamWidgetIds = Object.keys(
            this.registeredSymbolStreams
        ).filter((wk) => activeWidgetIds.indexOf(wk) > -1);

        for (const widgetKey of activeRegisteredSymbolStreamWidgetIds) {
            symbols.push(
                ...this.registeredSymbolStreams[widgetKey].symbols.filter(
                    (s) => s !== noSymbol
                )
            );
        }

        this.logger.debug('Registered widget ids are', {
            activeRegisteredWidgetIds,
            activeRegisteredSymbolStreamWidgetIds,
        });

        const ret = uniq(symbols);
        this.logger.debug('Registered symbols are', ret);

        return ret;
    }

    private removeStaleSymbols(
        dispatcher: ActionsDispatcher,
        adding: string[],
        removing: string[]
    ) {
        if (removing.length === 0) {
            return;
        }
        this.removedSymbols = this.removedSymbols.filter(
            (r) => !adding.includes(r.symbol)
        );
        const totalStoppedCount = removing.length + this.removedSymbols.length;
        const totalStaleCount = totalStoppedCount - RetainStaleSymbolsQty;
        const oldStaleCount = Math.min(
            totalStaleCount,
            this.removedSymbols.length
        );

        if (oldStaleCount > 0) {
            const symbolsToKill = orderBy(
                this.removedSymbols,
                ['time'],
                ['asc']
            ).slice(0, oldStaleCount);

            for (const s of symbolsToKill) {
                this.removedSymbols = this.removedSymbols.filter(
                    (k) => k.symbol !== s.symbol
                );

                dispatcher.dispatch(
                    createRemoveSymbolTilesStreamAction(s.symbol)
                );
            }
        }

        this.removedSymbols.push(
            ...removing.map((r) => ({ symbol: r, time: new Date().getTime() }))
        );
    }
}
